import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FileText, Download, Calendar, CheckCircle, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Quotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/quotes`);
        setQuotes(response.data);
      } catch (error) {
        console.error('Error fetching quotes:', error);
        toast.error('Erreur lors du chargement des devis');
      } finally {
        setLoading(false);
      }
    };
    fetchQuotes();
  }, []);

  const downloadQuote = async (quoteId, quoteNumber, format) => {
    try {
      const response = await axios.get(
        `${API}/quotes/${quoteId}/download/${format}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const extension = format === 'word' ? 'docx' : format === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `devis_${quoteNumber}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Devis téléchargé en ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error downloading quote:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, text: 'En attente', class: 'bg-yellow-100 text-yellow-800' },
      sent: { icon: FileText, text: 'Envoyé', class: 'bg-blue-100 text-blue-800' },
      accepted: { icon: CheckCircle, text: 'Accepté', class: 'bg-green-100 text-green-800' },
      rejected: { icon: XCircle, text: 'Refusé', class: 'bg-red-100 text-red-800' }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge.class}`} data-testid={`status-badge-${status}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const filteredQuotes = filter === 'all' 
    ? quotes 
    : quotes.filter(q => q.status === filter);

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center" data-testid="loading-quotes">
            <p className="text-muted-foreground">Chargement des devis...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-2" data-testid="quotes-page-title">
              Mes Devis
            </h1>
            <p className="text-base leading-relaxed text-muted-foreground">
              Consultez et téléchargez vos devis
            </p>
          </div>
          <Link
            to="/demander-devis"
            className="bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 shadow-lg shadow-accent/20 inline-flex items-center justify-center"
            data-testid="new-quote-btn"
          >
            Nouveau devis
          </Link>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          {[
            { id: 'all', name: 'Tous' },
            { id: 'pending', name: 'En attente' },
            { id: 'sent', name: 'Envoyés' },
            { id: 'accepted', name: 'Acceptés' },
            { id: 'rejected', name: 'Refusés' }
          ].map((filterOption) => (
            <button
              key={filterOption.id}
              onClick={() => setFilter(filterOption.id)}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                filter === filterOption.id
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                  : 'bg-white border border-input hover:bg-muted'
              }`}
              data-testid={`filter-${filterOption.id}`}
            >
              {filterOption.name}
            </button>
          ))}
        </div>

        {filteredQuotes.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center" data-testid="no-quotes">
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-6">
              {filter === 'all' ? 'Aucun devis pour le moment' : `Aucun devis ${filter === 'pending' ? 'en attente' : filter === 'sent' ? 'envoyé' : filter === 'accepted' ? 'accepté' : 'refusé'}`}
            </p>
            <Link
              to="/demander-devis"
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 shadow-lg shadow-primary/20 inline-flex items-center justify-center"
            >
              Créer un devis
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6" data-testid="quotes-list">
            {filteredQuotes.map((quote) => (
              <div
                key={quote.id}
                className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow"
                data-testid={`quote-card-${quote.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading font-semibold text-xl" data-testid={`quote-number-${quote.id}`}>
                        {quote.quote_number}
                      </h3>
                      {getStatusBadge(quote.status)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {quote.customer_name} • {quote.customer_email}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-primary" data-testid={`quote-total-${quote.id}`}>
                      {quote.total.toFixed(2)} DH
                    </p>
                    <p className="text-xs text-muted-foreground">TTC</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <Calendar className="w-4 h-4" />
                  <span>Créé le {new Date(quote.created_at).toLocaleDateString('fr-FR')}</span>
                  <span>•</span>
                  <span>Valide jusqu'au {new Date(quote.valid_until).toLocaleDateString('fr-FR')}</span>
                </div>

                <div className="border-t border-border pt-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    {quote.items.length} article{quote.items.length > 1 ? 's' : ''}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => downloadQuote(quote.id, quote.quote_number, 'pdf')}
                      className="flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-full text-sm font-medium transition-all"
                      data-testid={`download-pdf-${quote.id}`}
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </button>
                    <button
                      onClick={() => downloadQuote(quote.id, quote.quote_number, 'word')}
                      className="flex items-center gap-2 bg-white border border-input hover:bg-muted px-4 py-2 rounded-full text-sm font-medium transition-all"
                      data-testid={`download-word-${quote.id}`}
                    >
                      <Download className="w-4 h-4" />
                      Word
                    </button>
                    <button
                      onClick={() => downloadQuote(quote.id, quote.quote_number, 'excel')}
                      className="flex items-center gap-2 bg-white border border-input hover:bg-muted px-4 py-2 rounded-full text-sm font-medium transition-all"
                      data-testid={`download-excel-${quote.id}`}
                    >
                      <Download className="w-4 h-4" />
                      Excel
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};